// src/components/holidays/add.js

import { useContext, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import { InputContainer } from "uikit-react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";
import moment from "moment";

import { AppContexts } from "../../providers";
import { Form, FormRow, FormInputWrapper } from "../../ui";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  date: yup.string().required("Date is required"),
  type: yup.string().oneOf(["national", "custom"]).required(),
  scope: yup.string().oneOf(["company", "selected"]).required(),
});

const defaultValues = {
  name: "",
  date: "", // YYYY-MM-DD
  type: "national",
  scope: "company",
  observed: true,
  is_recurring: true, // yearly
  notes: "",
};

const HolidayAdd = () => {
  const holidaysContext = useContext(AppContexts.HolidaysContext);
  const { user } = useContext(AppContexts.AuthContext);

  const {
    holiday,
    setHoliday,
    flagIsNew,
    insertHoliday,
    updateHoliday,
    flagIsProcessing,
  } = holidaysContext;

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const type = useMemo(() => getValues("type"), [watch("type")]);
  const scope = useMemo(() => getValues("scope"), [watch("scope")]);

  useEffect(() => {
    clearErrors();

    // hydrate form from context holiday
    Object.keys(defaultValues).forEach((k) => {
      setValue(k, holiday?.[k] ?? defaultValues[k]);
    });

    // if editing and holiday has datetime, normalize to YYYY-MM-DD
    if (holiday?.date && holiday.date.includes("T")) {
      setValue("date", moment(holiday.date).format("YYYY-MM-DD"));
    }

    if (!holiday || Object.keys(holiday).length === 0) {
      reset(defaultValues);
    }
  }, [JSON.stringify(holiday)]);

  const handleSave = useCallback(
    async (formdata) => {
      const payload = {
        ...formdata,
        account_id: user.account_id,
      };

      try {
        const ok = await (flagIsNew
          ? insertHoliday(payload)
          : updateHoliday({ ...payload, id: holiday.id }));

        if (ok) {
          UIkit.modal("#modal-holiday-add").hide();
          UIkit.notification(flagIsNew ? "Holiday added" : "Holiday updated", "success");
          reset(defaultValues);
          setHoliday({});
        } else {
          UIkit.notification("Failed", "error");
        }
      } catch (e) {
        UIkit.notification("Failed", "error");
      }
    },
    [flagIsNew, holiday?.id, user?.account_id],
  );

  const handleError = useCallback((err) => console.log(err), []);

  return (
    <div
      id="modal-holiday-add"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
            onClick={() => setHoliday({})}
          ></button>

          <div className="uk-modal-header">
            <h2 className="uk-modal-title">
              {flagIsNew ? "Add" : "Edit"} Holiday
            </h2>
          </div>

          <div className="uk-modal-body">
            <div className="add-contact-form">
              <Form onSubmit={handleSubmit(handleSave, handleError)} data-uk-grid="true">
                <FormRow>
                  <FormInputWrapper label="Holiday Name*">
                    <InputContainer>
                      <input
                        className={classnames("uk-input", { error: errors.name })}
                        type="text"
                        placeholder="e.g. Thanksgiving"
                        {...register("name")}
                      />
                    </InputContainer>
                    {errors.name && <div className="uk-text-danger uk-margin-small-top">{errors.name.message}</div>}
                  </FormInputWrapper>

                  <FormInputWrapper label="Date*">
                    <InputContainer>
                      <input
                        className={classnames("uk-input", { error: errors.date })}
                        type="date"
                        {...register("date")}
                      />
                    </InputContainer>
                    {errors.date && <div className="uk-text-danger uk-margin-small-top">{errors.date.message}</div>}
                  </FormInputWrapper>
                </FormRow>

                <FormRow>
                  <FormInputWrapper label="Type*">
                    <InputContainer>
                      <select className={classnames("uk-select", { error: errors.type })} {...register("type")}>
                        <option value="national">National</option>
                        <option value="custom">Custom</option>
                      </select>
                    </InputContainer>
                  </FormInputWrapper>

                  <FormInputWrapper label="Applies To*">
                    <InputContainer>
                      <select className={classnames("uk-select", { error: errors.scope })} {...register("scope")}>
                        <option value="company">Company-wide</option>
                        <option value="selected">Selected staff/groups</option>
                      </select>
                    </InputContainer>
                  </FormInputWrapper>
                </FormRow>

                {/* Keep this simple for v1: store scope, add targeting later */}
                {scope === "selected" && (
                  <div className="uk-alert uk-alert-primary uk-margin-small" uk-alert="true">
                    For v1, you can save this as “Selected” and wire targeting later (roles/teams).
                  </div>
                )}

                <FormRow>
                  <FormInputWrapper label="Observed">
                    <label className="uk-switch uk-display-inline-flex uk-flex-middle">
                      <input type="checkbox" {...register("observed")} />
                      <span className="uk-switch-slider" />
                      <span className="uk-margin-small-left secondary">
                        {watch("observed") ? "Yes" : "No"}
                      </span>
                    </label>
                  </FormInputWrapper>

                  <FormInputWrapper label="Recurs Yearly">
                    <label className="uk-switch uk-display-inline-flex uk-flex-middle">
                      <input type="checkbox" {...register("is_recurring")} />
                      <span className="uk-switch-slider" />
                      <span className="uk-margin-small-left secondary">
                        {watch("is_recurring") ? "Yes" : "No"}
                      </span>
                    </label>
                  </FormInputWrapper>
                </FormRow>

                <FormRow className="uk-width-1-1">
                  <FormInputWrapper label="Notes (optional)" size="two">
                    <InputContainer>
                      <textarea className="uk-textarea" rows="3" {...register("notes")} />
                    </InputContainer>
                  </FormInputWrapper>
                </FormRow>

                <div>
                  <button className="uk-button hms-btn hms-red-btn" type="submit">
                    {flagIsProcessing ? <div data-uk-spinner="ratio: 0.5;"></div> : "Save"}
                  </button>
                  <button
                    className="uk-button hms-btn uk-offcanvas-close"
                    type="button"
                    onClick={() => setHoliday({})}
                  >
                    Cancel
                  </button>
                </div>
              </Form>

              {!flagIsNew && (
                <>
                  {holiday?.updated_at && (
                    <div className="s-msg-last-updated">
                      Last Updated at:{" "}
                      {moment.utc(holiday.updated_at).local().format("MM/DD/YYYY hh:mm a")}
                    </div>
                  )}
                  {holiday?.created_at && (
                    <div className="s-msg-last-updated">
                      Added at:{" "}
                      {moment.utc(holiday.created_at).local().format("MM/DD/YYYY hh:mm a")}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolidayAdd;